import React from 'react';
import { ConfirmDialog } from './confirm-dialog';

/**
 * OperationConfirmDialog — fonte ÚNICA de confirmação de operações.
 *
 * Por que existe: antes, cada tela escrevia o texto de confirmação à mão (3 formatos
 * diferentes: AlertDialog inline, ConfirmDialog inline e window.confirm nativo), e o
 * texto divergia entre telas. Este wrapper centraliza a REDAÇÃO: dado `action` + `ops`
 * + `date` (+ valor/detalhes opcionais), ele monta sempre um corpo padronizado que
 * carrega O QUE está sendo feito (data + ops + tipo). Só muda quem chama; a base
 * (ConfirmDialog -> Radix Dialog) permanece.
 *
 * Quem consome (gestor): checkout-action-buttons (whatsapp/compra/inserir-db),
 * indrive-resolution (push), baixa-item-card e entrego-baixa-item-card (baixa).
 *
 * ponytail: cópia idêntica vive em dashboard-clientes/src/components/ui/operation-confirm-dialog.tsx
 * (repos separados, sem pacote compartilhado). Editou aqui? Sincronize lá.
 */

export type OperationAction =
  | 'compra'      // iniciar processo de compra do checkout
  | 'baixa'       // liquidação no banco de dados (inclui criar CNAB de baixa)
  | 'checkout'    // finalizar/solicitar checkout do romaneio
  | 'whatsapp'    // enviar mensagens aos entregadores
  // TEMP-SPLIT-OFERTA: registro passivo de ofertas (entrego). Franquias fora do
  // piloto de split seguem recebendo envio ativo por este mesmo botão.
  // PILOTO ENCERRADO (2026-10-02): sem uso hoje — o entrego voltou à ação 'whatsapp'.
  | 'registro-ofertas'
  | 'push'        // push de operação externa (ex.: Indrive)
  | 'inserir-db'  // submeter a remessa de compra ao DB
  | 'download';   // baixar arquivo de pagamentos (liquidados/não liquidados)

// Redação por ação — única fonte de verdade do título e do verbo da confirmação.
const ACTION_LABELS: Record<OperationAction, { title: string; verbo: string }> = {
  compra: { title: 'Confirmar compra', verbo: 'Realizar compra' },
  baixa: { title: 'Confirmar baixa', verbo: 'Realizar baixa' },
  checkout: { title: 'Confirmar checkout', verbo: 'Finalizar checkout' },
  whatsapp: { title: 'Enviar mensagens?', verbo: 'Enviar mensagens via WhatsApp' },
  // TEMP-SPLIT-OFERTA
  'registro-ofertas': { title: 'Registrar ofertas?', verbo: 'Registrar ofertas (franquias fora do piloto recebem envio ativo)' },
  push: { title: 'Confirmar push', verbo: 'Realizar push' },
  'inserir-db': { title: 'Inserir no banco?', verbo: 'Submeter remessa de compra ao DB' },
  download: { title: 'Confirmar download', verbo: 'Baixar arquivo' },
};

// Normaliza qualquer data para YYYY-MM-DD (strings ISO via slice; Date via toISOString).
// Dayjs/Moment: o chamador formata antes (passa string).
function normalizeDate(date?: string | Date): string | undefined {
  if (!date) return undefined;
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
  }
  return String(date).slice(0, 10);
}

function formatBRL(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

export interface OperationConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  action: OperationAction;
  ops: string;
  date?: string | Date;
  amount?: number; // em centavos
  details?: { label: string; value: string }[];
  confirmDisabled?: boolean;
  loading?: boolean;
}

export function OperationConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  action,
  ops,
  date,
  amount,
  details,
  confirmDisabled = false,
  loading = false,
}: OperationConfirmDialogProps) {
  const { title, verbo } = ACTION_LABELS[action];
  const normDate = normalizeDate(date);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title={title}
      confirmText={loading ? 'Processando...' : 'Confirmar'}
      cancelText="Cancelar"
      confirmDisabled={confirmDisabled || loading}
    >
      <p className="font-medium text-foreground">{verbo}</p>
      <p className="text-sm text-muted-foreground">
        OPS: {ops.toUpperCase()}
        {normDate ? `, Data: ${normDate}` : ''}
      </p>
      {amount != null && (
        <p className="text-sm text-muted-foreground">Valor: {formatBRL(amount)}</p>
      )}
      {details?.map((d) => (
        <p key={d.label} className="text-sm text-muted-foreground">
          {d.label}: {d.value}
        </p>
      ))}
    </ConfirmDialog>
  );
}
